import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import AbortControllerManager from '@utils/abortController';
import { ClientSourcePathHeaderOptions, CustomHTTPHeaders } from '@constants/http';
import { PipelineOutputType } from '@constants/pipelines';
import {
  addPromptExplorerInfoResult as addPromptExplorerInfoResultAction,
  queryPromptExplorer as queryPromptExplorerAction,
  resetChatResults as resetChatResultsAction,
  resetResults,
  resetSearchResultsPromptExplorer as resetSearchResultsPromptExplorerAction,
  search as searchAction,
  sendChatQuery,
  setQueryPipelineParams as setQueryPipelineParamsAction,
} from '@redux/actions/searchActions';
import {
  chatQueryStatusSelector,
  chatResultsSelector,
  queryPipelineParamsSelector,
  searchResultSelector,
  searchStatusSelector,
} from '@redux/selectors/searchSelectors';
import { IPipelineQueryParams, MetadataFiltersValue, RequestController } from '@redux/types/types';
import { useAppDispatch } from './redux';
import { useCurrentRoute } from './useCurrentRoute';
import usePipelineFeedback from './usePipelineFeedback';

const CLIENT_SOURCE_PATH_CONFIG: Record<string, ClientSourcePathHeaderOptions> = {
  playground: ClientSourcePathHeaderOptions.PLAYGROUND,
  builder: ClientSourcePathHeaderOptions.BUILDER,
  shared_prototypes: ClientSourcePathHeaderOptions.SHARED_PROTOTYPES,
};

interface ISharedQueryParams {
  pipelineName: string;
  query: string;
  filters?: Record<string, MetadataFiltersValue>;
  params?: IPipelineQueryParams;
  sessionId?: string;
  viewPrompts?: boolean;
  isExternal?: boolean;
}

// TODO: Maybe move each query type logic into it's own hook
const usePipelineQuery = () => {
  const dispatch = useAppDispatch();
  const { resetProvidedFeedbackStatus } = usePipelineFeedback();
  const {
    location: { pathname: locationPathname },
  } = useCurrentRoute();
  const queryPipelineParams = useSelector(queryPipelineParamsSelector);

  const requestHeaders = useMemo(() => {
    const key =
      Object.keys(CLIENT_SOURCE_PATH_CONFIG).find((pathKey) =>
        locationPathname.includes(pathKey),
      ) ?? ClientSourcePathHeaderOptions.PLAYGROUND;
    const clientSourcePath = CLIENT_SOURCE_PATH_CONFIG[key];

    return {
      [CustomHTTPHeaders.X_CLIENT_SOURCE_PATH]: clientSourcePath,
    };
  }, [locationPathname]);

  const setQueryPipelineParams = (params: IPipelineQueryParams) => {
    dispatch(setQueryPipelineParamsAction(params));
  };

  // Search
  const searchResult = useSelector(searchResultSelector);
  const searchStatus = useSelector(searchStatusSelector);

  const resetSearchResults = () => {
    AbortControllerManager.getInstance().abortAll();
    dispatch(resetResults);
    resetProvidedFeedbackStatus();
  };

  // Chat
  const chatResults = useSelector(chatResultsSelector);
  const chatQueryStatus = useSelector(chatQueryStatusSelector);

  const resetChatResults = () => {
    AbortControllerManager.getInstance().abortAll();
    dispatch(resetChatResultsAction);
    resetProvidedFeedbackStatus();
  };

  // Prompt Explorer
  const resetSearchResultsPromptExplorer = (playgroundId: string) => {
    AbortControllerManager.getInstance().abort(
      `${RequestController.PROMPT_EXPLORER_QUERY}_${playgroundId}`,
    );
    dispatch(resetSearchResultsPromptExplorerAction(playgroundId));
    resetProvidedFeedbackStatus();
  };

  // Query methods

  const search = useCallback(
    async (
      params: ISharedQueryParams & {
        pipelineOutputType?: PipelineOutputType;
        files?: string[];
      },
    ) => {
      const { pipelineOutputType } = params;
      const enableStreaming =
        pipelineOutputType === PipelineOutputType.CHAT ||
        pipelineOutputType === PipelineOutputType.GENERATIVE;

      dispatch(searchAction({ streaming: enableStreaming, headers: requestHeaders, ...params }));
    },
    [dispatch, requestHeaders],
  );

  const chat = async (
    params: ISharedQueryParams & {
      sessionId: string;
      params: IPipelineQueryParams;
      files: string[];
    },
  ) => {
    dispatch(
      sendChatQuery({
        streaming: true,
        viewPrompts: true,
        headers: requestHeaders,
        ...params,
      }),
    );
  };

  const queryPromptExplorer = async (
    params: ISharedQueryParams & {
      pipelineOutputType: PipelineOutputType;
      sessionId: string;
      playgroundId: string;
    },
  ) => {
    dispatch(queryPromptExplorerAction({ streaming: true, viewPrompts: true, ...params }));
  };

  // TODO: Restructure query result into type based results (e.g ResultType, InfoType, ErrorType)
  const addPromptExplorerInfoResult = ({
    playgroundId,
    infoText,
  }: {
    playgroundId: string;
    infoText: string;
  }) => {
    dispatch(addPromptExplorerInfoResultAction({ playgroundId, infoText }));
  };

  return {
    queryPipelineParams,
    setQueryPipelineParams,
    // Search
    search,
    searchResult,
    searchStatus,
    resetSearchResults,
    // Chat
    chat,
    resetChatResults,
    chatResults,
    chatQueryStatus,
    // Prompt Explorer
    queryPromptExplorer,
    addPromptExplorerInfoResult,
    resetSearchResultsPromptExplorer,
  };
};

export default usePipelineQuery;
